<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark>mdi-arrow-left</v-icon>
          </v-btn>
          {{ categoryInfo ? `Edit Weekly Rewards - ${categoryInfo.description}` : 'Edit Weekly Rewards' }}
        </v-card-title>

        <v-card-text>
          <!-- Category Information Display -->
          <v-row>
            <v-col sm="6">
              <v-autocomplete
                :value="categoryInfo ? categoryInfo.id : null"
                :items="[categoryInfo]"
                item-text="description"
                item-value="id"
                outlined
                label="Category *"
                prepend-inner-icon="mdi-tag"
                disabled
              />
            </v-col>
          </v-row>

          <!-- All Rewards List (Existing + New) -->
          <template v-if="allRewards.length > 0">
            <v-row
              v-for="(item, index) in allRewards"
              :key="`item_${item.id || 'new_' + index}`"
              class="mb-6"
            >
              <!-- <v-col sm="2">
                <v-text-field
                  v-model="item.name"
                  outlined
                  label="Reward Name"
                  prepend-inner-icon="mdi-gift"
                />
              </v-col> -->

              <v-col sm="2" v-if="!item.store_item_id">
                <v-text-field
                  v-model="item.coin"
                  outlined
                  label="Coin Value"
                  type="number"
                  prepend-inner-icon="mdi-bitcoin"
                  @input="onCoinChange(item)"
                />
              </v-col>

              <v-col sm="2" v-if="!item.coin || item.coin === '' || item.coin === 0">
                <v-autocomplete
                  v-model="item.store_item_id"
                  :items="storeItems"
                  item-text="name"
                  item-value="id"
                  outlined
                  label="Store Item"
                  prepend-inner-icon="mdi-package-variant"
                  clearable
                  @change="onStoreItemChange(item)"
                />
              </v-col>

              <v-col sm="2" v-if="!item.coin || item.coin === '' || item.coin === 0">
                <v-text-field
                  v-model="item.expiry_date"
                  outlined
                  label="Expiry Date"
                  type="date"
                  prepend-inner-icon="mdi-calendar-clock"
                  :min="minDate"
                />
              </v-col>

              <!-- <v-col sm="2">
                <input
                  type="file"
                  :id="`file-${index}`"
                  :ref="`file-${index}`"
                  @change="onFileChange($event, index)"
                  accept="image/*"
                />

                <v-btn
                  x-small
                  class="reset_image my-1"
                  color="red"
                  dark
                  @click="resetImage(index)"
                >
                  Remove
                </v-btn>

                <div v-if="item.image_file || item.image_path" class="mt-3">
                  <label>Image Preview</label>
                  <br />
                  <img
                    :src="item.image_file || `${mediaUrl}${item.image_path}`"
                    style="height: 80px; width: 80px; object-fit: cover"
                  />
                </div>
              </v-col> -->

              <!-- Delete Button -->
              <v-col sm="2" class="d-flex align-center">
                <v-btn icon small color="error" @click="deleteReward(index)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </template>

          <!-- Add Reward Button -->
          <v-row>
            <v-col>
              <v-btn color="green" dark small @click="addNewReward">
                <v-icon left>mdi-plus</v-icon> Add Reward
              </v-btn>
            </v-col>
          </v-row>

          <v-divider class="my-5" />

          <!-- Save Button -->
          <v-btn
            rounded
            color="primary"
            :loading="isLoading"
            :disabled="isLoading"
            @click="save"
          >
            Update Weekly Rewards
          </v-btn>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "WeeklyRewardCategoryEditPage",
  data() {
    return {
      categoryInfo: null,
      allRewards: [],
      storeItems: [],
      isLoading: false,
      deletedRewards: [], // Track rewards to be deleted
      rules: {
        required: (value) => !!value || "Required.",
      },
    };
  },
  computed: {
    minDate() {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split("T")[0];
    },
  },
  mounted() {
    this.loadCategoryRewards();
    this.getStoreItems();
  },
  methods: {
    onCoinChange(item) {
      // Clear store_item_id when coin value is entered
      if (item.coin && item.coin !== '' && item.coin !== 0) {
        item.store_item_id = null;
      }
    },

    onStoreItemChange(item) {
      // Clear coin value when store item is selected
      if (item.store_item_id) {
        item.coin = '';
      }
    },
    async loadCategoryRewards() {
      this.isLoading = true;
      let encryptedId = this.$route.params.categoryId;

      try {
        const response = await axios.get(`/weekly_rewards/category/${encryptedId}`);
        this.allRewards = response.data.data.map(reward => ({
          ...reward,
          image_file: null,
          file: null,
        }));
        this.categoryInfo = response.data.category;
      } catch (error) {
        console.error("Error loading category rewards:", error);
        this.$toast?.error("Failed to load category rewards");
      }

      this.isLoading = false;
    },

    async getStoreItems() {
      try {
        const response = await axios.get("/masters?context=reward_category");
        this.storeItems = response.data.storeItem || [];
      } catch (e) {
        console.error("Error loading store items:", e);
      }
    },

    addNewReward() {
      this.allRewards.push({
        name: "",
        coin: "",
        store_item_id: null,
        expiry_date: "",
        file: null,
        image_file: null,
      });
    },

    deleteReward(index) {
      const reward = this.allRewards[index];
      if (reward.id) {
        this.deletedRewards.push(reward.id);
      }
      this.allRewards.splice(index, 1);
    },

    onFileChange(event, index) {
      const file = event.target.files[0];
      if (file) {
        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
          this.$toast?.error('Please select a valid image file (JPEG, PNG, JPG, GIF)');
          return;
        }

        // Validate file size (2MB)
        if (file.size > 2 * 1024 * 1024) {
          this.$toast?.error('File size must be less than 2MB');
          return;
        }

        this.allRewards[index].file = file;
        this.allRewards[index].image_file = URL.createObjectURL(file);
      }
    },

    resetImage(index) {
      this.allRewards[index].file = null;
      this.allRewards[index].image_file = null;
      if (this.$refs[`file-${index}`] && this.$refs[`file-${index}`][0]) {
        this.$refs[`file-${index}`][0].value = null;
      }
    },

    async save() {
      try {
        this.isLoading = true;

        // Delete removed rewards first
        for (let rewardId of this.deletedRewards) {
          await axios.post(`/weekly_rewards/delete/${this.encryptIt(rewardId)}`);
        }

        // Separate existing and new rewards
        const existingRewards = this.allRewards.filter(reward => reward.id);
        const newRewards = this.allRewards.filter(reward => !reward.id);

        // Update existing rewards
        for (let reward of existingRewards) {
          const updateData = {
            category_id: this.categoryInfo.id,
            name: reward.name || "",
            coin: reward.coin || "",
            store_item_id: reward.store_item_id || "",
            expiry_date: reward.expiry_date || "",
            is_active: reward.is_active !== undefined ? reward.is_active : 1,
          };

          // Handle file upload for existing rewards
          if (reward.file) {
            const formData = new FormData();
            Object.keys(updateData).forEach(key => {
              formData.append(key, updateData[key]);
            });
            formData.append('image_path', reward.file);

            await axios.patch(`/weekly_rewards/${this.encryptIt(reward.id)}`, formData, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } else {
            await axios.patch(`/weekly_rewards/${this.encryptIt(reward.id)}`, updateData);
          }
        }

        // Create new rewards if any
        if (newRewards.length > 0) {
          const formData = new FormData();
          formData.append("category_id", this.categoryInfo.id);

          newRewards.forEach((reward, index) => {
            formData.append(`weekly_rewards[${index}][name]`, reward.name || "");
            formData.append(`weekly_rewards[${index}][coin]`, reward.coin || "");
            formData.append(`weekly_rewards[${index}][store_item_id]`, reward.store_item_id || "");
            formData.append(`weekly_rewards[${index}][expiry_date]`, reward.expiry_date || "");

            if (reward.file) {
              formData.append(`weekly_rewards[${index}][image_path]`, reward.file);
            }
          });

          await axios.post("/weekly_rewards", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }

        this.$toast?.success("Weekly Rewards updated successfully!");
        this.$router.push("/weekly-rewards");

      } catch (error) {
        console.error("Error updating rewards:", error);
        if (error.response && error.response.data && error.response.data.errors) {
          const errors = error.response.data.errors;
          Object.keys(errors).forEach(key => {
            this.$toast?.error(errors[key][0]);
          });
        } else {
          this.$toast?.error("Failed to update weekly rewards");
        }
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
input[type="file"] {
  margin-bottom: 10px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
}
.reset_image {
  margin-top: 5px;
}

.reward-panel {
  border: 1px solid #e0e0e0 !important;
}

.reward-panel:hover {
  border-color: #26a69a !important;
}

.new-reward-card {
  transition: all 0.3s ease;
}

.new-reward-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.v-expansion-panel-header {
  min-height: 60px !important;
}

.v-expansion-panel-content >>> .v-expansion-panel-content__wrap {
  padding: 0;
}
</style>